import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import customFieldsService from '../../services/customFieldsService';

const FIELDS_KEY = ['customFields'];

/** Perguntas do dono + mutations. Criar e apagar mudam o uso do plano: o snapshot de limites é invalidado junto. */
export function useCustomFields() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: FIELDS_KEY,
    queryFn: customFieldsService.list,
    staleTime: 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: FIELDS_KEY });
    queryClient.invalidateQueries({ queryKey: ['entitlements'] });
  };

  const createMutation = useMutation({ mutationFn: customFieldsService.create, onSuccess: invalidate });
  const createSystemMutation = useMutation({
    mutationFn: ({ systemKey, payload }) => customFieldsService.createSystem(systemKey, payload),
    onSuccess: invalidate,
  });
  const renameMutation = useMutation({ mutationFn: ({ id, label }) => customFieldsService.rename(id, label), onSuccess: invalidate });
  const setActiveMutation = useMutation({ mutationFn: ({ id, isActive }) => customFieldsService.setActive(id, isActive), onSuccess: invalidate });
  // Otimista: a View solta o cartão na hora, e ele não pode voltar ao lugar antigo até a resposta chegar.
  const reorderMutation = useMutation({
    mutationFn: customFieldsService.reorder,
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: FIELDS_KEY });
      const previous = queryClient.getQueryData(FIELDS_KEY);
      queryClient.setQueryData(FIELDS_KEY, (fields = []) => [...fields].sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id)));
      return { previous };
    },
    onError: (_error, _ids, context) => queryClient.setQueryData(FIELDS_KEY, context.previous),
    onSettled: invalidate,
  });
  const removeMutation = useMutation({ mutationFn: customFieldsService.remove, onSuccess: invalidate });

  return {
    fields: query.data || [],
    loading: query.isLoading,
    error: query.error,
    reload: query.refetch,
    create: createMutation.mutateAsync,
    createSystem: createSystemMutation.mutateAsync,
    rename: renameMutation.mutateAsync,
    setActive: setActiveMutation.mutateAsync,
    reorder: reorderMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
  };
}

/** Perguntas do agendamento manual. Só busca com profissional, local e serviço escolhidos. */
export function useBookingCustomFields({ professionalId, placeId, serviceId }) {
  const query = useQuery({
    queryKey: ['customFieldsForBooking', professionalId, placeId, serviceId],
    queryFn: () => customFieldsService.forBooking({ professionalId, placeId, serviceId }),
    enabled: !!professionalId && !!placeId && !!serviceId,
    staleTime: 60 * 1000,
  });

  return { fields: query.data || [], loading: query.isLoading, error: query.error, reload: query.refetch };
}

/** Respostas de um agendamento + salvar e resolver divergência. */
export function useAppointmentAnswers(appointmentId) {
  const queryClient = useQueryClient();
  const key = ['appointmentAnswers', appointmentId];

  const query = useQuery({
    queryKey: key,
    queryFn: () => customFieldsService.getAppointmentAnswers(appointmentId),
    enabled: !!appointmentId,
    staleTime: 60 * 1000,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
    queryClient.invalidateQueries({ queryKey: ['personCustomFields'] });
  };

  const saveMutation = useMutation({
    mutationFn: (answers) => customFieldsService.saveAppointmentAnswers(appointmentId, answers),
    onSuccess: invalidate,
  });
  const resolveMutation = useMutation({
    mutationFn: ({ valueId, action }) => customFieldsService.resolveDivergence(valueId, action),
    onSuccess: invalidate,
  });

  return {
    data: query.data || null,
    loading: query.isLoading,
    error: query.error,
    reload: query.refetch,
    save: saveMutation.mutateAsync,
    saving: saveMutation.isPending,
    resolve: resolveMutation.mutateAsync,
  };
}

/** Valores atuais e divergências da pessoa. Resolver atualiza a ficha. */
export function usePersonCustomFields(personId) {
  const queryClient = useQueryClient();
  const key = ['personCustomFields', personId];

  const query = useQuery({
    queryKey: key,
    queryFn: () => customFieldsService.getPersonFields(personId),
    enabled: !!personId,
    staleTime: 60 * 1000,
  });

  const resolveMutation = useMutation({
    mutationFn: ({ valueId, action }) => customFieldsService.resolveDivergence(valueId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ['person', personId] });
      queryClient.invalidateQueries({ queryKey: ['appointmentAnswers'] });
    },
  });

  return {
    data: query.data || null,
    loading: query.isLoading,
    error: query.error,
    reload: query.refetch,
    resolve: resolveMutation.mutateAsync,
  };
}
